import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/_module/components/ui/card';
import { Button } from '@/app/_module/components/ui/button';
import { Input } from '@/app/_module/components/ui/input';
import { Label } from '@/app/_module/components/ui/label';
import { Textarea } from '@/app/_module/components/ui/textarea';
import { Plus, X, Info, CalendarIcon, Eye, EyeOff, Shield, PhoneCall } from 'lucide-react';
import { Badge } from '@/app/_module/components/ui/badge';
import { FormItem, FormLabel, FormControl } from '@/app/_module/components/ui/form';
import { Calendar } from '@/app/_module/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/app/_module/components/ui/popover';
import customFieldsService, { type CustomFieldDefinition } from '@/app/_module/services/customFieldsService';
import { describeCustomKey, KEY_FIELD_CANONICALS, canonicalCustomFieldLabel } from '@/app/_module/utils/customFieldAliases';
import { useKeyFieldsOnly } from '@/app/_module/hooks/useKeyFieldsOnly';

// Agents open this section for three fields — Carrier, Annual Premium, Monthly
// Premium — out of an average of 11 (max 166) per lead. Show those by default;
// "Show all fields" reveals the rest. Remembered per browser, like the section
// visibility in UnifiedGeneralCard — see useKeyFieldsOnly (shared with the
// filter / sort pickers on the leads page).
// Alternate numbers ride in as custom fields ("Phone 2", "Mobile", "Fax").
// One list, two jobs: keep them out of the sensitive-value mask, and decide
// which fields get a dial button.
const PHONE_LABEL_HINTS = ['phone', 'mobile', 'cell', 'fax'];
const isPhoneLabel = (label?: string) => {
  const l = (label || '').toLowerCase();
  return PHONE_LABEL_HINTS.some((h) => l.includes(h));
};
/** Dialable = a phone-ish label AND a US-shaped value. Returns E.164-ish digits. */
const dialableDigits = (label: string | undefined, value: string): string | null => {
  if (!isPhoneLabel(label)) return null;
  const d = (value || '').replace(/\D/g, '');
  if (d.length === 10) return d;
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  return null;
};
const formatPhoneDisplay = (digits: string) =>
  `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;

const KEY_FIELD_LABELS = KEY_FIELD_CANONICALS.map((c) => canonicalCustomFieldLabel(c) ?? c).join(', ');

interface CustomField {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone';
  required?: boolean;
}

interface CustomFieldsSectionProps {
  isEditing?: boolean;
  leadId?: string;
  bare?: boolean; // When true, render fields only without Card wrapper
  onAddField?: () => void; // External trigger for "Add Field" (used by unified card)
  // Dial an alternate number off a custom field. Only surfaces the button when
  // the host actually has a call path — /m has no VoiceDeviceProvider, so it
  // passes nothing and no button renders.
  onCallNumber?: (phone: string) => void;
}

export const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({
  isEditing = true,
  leadId,
  bare = false,
  onAddField,
  onCallNumber,
}) => {
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<'text' | 'textarea' | 'number' | 'date' | 'email' | 'phone'>('text');

  const form = useFormContext();

  // Watch customFields for reactive updates
  const watchedCustomFields = form.watch('customFields') || {};

  // Track if we've done initial load
  const [hasInitializedFields, setHasInitializedFields] = useState(false);

  // Track which sensitive fields have been revealed
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set());

  // Key-fields-only view (default on); one preference shared with the pickers.
  const [keyFieldsOnly, setKeyFieldsOnly] = useKeyFieldsOnly();

  // Load custom fields from the lead's customFields JSON data (only on initial load or leadId change)
  useEffect(() => {
    const leadCustomFields = form.getValues('customFields') || {};

    // Ensure customFields is a plain object, not an array
    if (Array.isArray(leadCustomFields) || typeof leadCustomFields !== 'object') {
      setCustomFields([]);
      setHasInitializedFields(true);
      return;
    }

    // Convert the lead's customFields object into field definitions
    // Filter out _meta keys (used below to restore field metadata) and internal
    // markers like _sourceAgencyId / _originalLeadStat, which aren't lead details.
    // Edits spread the whole object, so hidden keys are kept on save.
    const fieldsFromLead: CustomField[] = Object.keys(leadCustomFields)
      .filter(key => !key.startsWith('_'))
      .map(key => {
        const value = leadCustomFields[key];
        const meta = leadCustomFields[`_meta_${key}`];

        // Strip "unmapped." prefix from field names for cleaner display
        const cleanKey = key.startsWith('unmapped.') ? key.replace('unmapped.', '') : key;

        // Use stored metadata if available, otherwise infer
        let type: CustomField['type'] = 'text';
        let label = cleanKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

        if (meta && typeof meta === 'object') {
          type = meta.type || type;
          label = meta.label || label;
        } else {
          // Infer type from value if no metadata
          if (typeof value === 'number') type = 'number';
          else if (typeof value === 'string' && value.includes('@')) type = 'email';
          else if (typeof value === 'string' && value.length > 100) type = 'textarea';
        }

        return {
          id: key,
          name: key,
          label,
          type,
          required: false,
        };
      });

    setCustomFields(fieldsFromLead);
    setHasInitializedFields(true);
  }, [leadId, form]);

  // Add a new custom field (lead-specific, no API call)
  const handleAddField = () => {
    if (!newFieldName.trim() || !newFieldLabel.trim()) return;

    const fieldName = newFieldName.toLowerCase().replace(/[^a-z0-9]/g, '_');

    // Check if field already exists
    if (customFields.some(f => f.name === fieldName)) {
      alert('A field with this name already exists');
      return;
    }

    const newField: CustomField = {
      id: fieldName,
      name: fieldName,
      label: newFieldLabel,
      type: newFieldType,
      required: false,
    };

    // Add to state
    setCustomFields(prev => [...prev, newField]);

    // Ensure customFields is an object structure in the form
    const currentCustomFields = form.getValues('customFields') || {};
    if (Array.isArray(currentCustomFields) || typeof currentCustomFields !== 'object') {
      form.setValue('customFields', {}, { shouldDirty: true });
    }

    // Store field with metadata (value + _meta for label/type)
    const defaultValue = newFieldType === 'number' ? 0 : '';
    const updatedCustomFields = {
      ...form.getValues('customFields'),
      [fieldName]: defaultValue,
      [`_meta_${fieldName}`]: { label: newFieldLabel, type: newFieldType }
    };
    form.setValue('customFields', updatedCustomFields, { shouldDirty: true });

    // Clear inputs but keep form open for adding more fields
    setNewFieldName('');
    setNewFieldLabel('');
    setNewFieldType('text');
  };

  // Remove a custom field (lead-specific, no API call)
  const handleRemoveField = (fieldId: string) => {
    const field = customFields.find(f => f.id === fieldId);
    if (!field) return;

    // Get current customFields and remove field + its metadata
    const currentCustomFields = form.getValues('customFields') || {};
    const updatedCustomFields = { ...currentCustomFields };
    delete updatedCustomFields[field.name];
    delete updatedCustomFields[`_meta_${field.name}`];

    form.setValue('customFields', updatedCustomFields, { shouldDirty: true });
    setCustomFields(prev => prev.filter(f => f.id !== fieldId));
  };

  // Format phone number as (xxx) xxx-xxxx
  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Limit to 10 digits
    const limited = digits.slice(0, 10);

    // Format based on length
    if (limited.length === 0) return '';
    if (limited.length <= 3) return `(${limited}`;
    if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
  };

  // Handle phone field change with formatting
  const handlePhoneChange = (fieldName: string, value: string) => {
    const formatted = formatPhoneNumber(value);
    const currentCustomFields = form.getValues('customFields') || {};
    form.setValue('customFields', {
      ...currentCustomFields,
      [fieldName]: formatted
    }, { shouldDirty: true });
  };

  // Get min/max dates for date inputs
  const getDateConstraints = () => {
    const today = new Date();
    const maxDate = today.toISOString().split('T')[0]; // Today
    const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate())
      .toISOString().split('T')[0]; // 120 years ago
    return { minDate, maxDate };
  };

  // Detect and mask sensitive data in field values (SSN, DL, credit cards, bank accounts)
  // Matches backend DLP patterns from dlp.utils.ts (except DOB which stays unmasked per preference)
  // Uses both value patterns AND field label context based on real prod field names:
  //   SSN, Ssn, Social Security, LICENSE, Routing, Route, Account, Acct, Bank Name, etc.
  const maskValue = (value: string, fieldLabel?: string): { masked: string; isSensitive: boolean; type?: string } => {
    if (!value || typeof value !== 'string') return { masked: String(value ?? ''), isSensitive: false };
    const trimmed = value.trim();
    if (!trimmed) return { masked: '', isSensitive: false };
    const labelLower = (fieldLabel || '').toLowerCase();

    // Skip masking for fields that are clearly NOT sensitive based on their label
    // 'phone'/'mobile'/'cell'/'fax' matter: a bare 10-digit phone hits the
    // >=10-digit fallback below and masks as an Account Number.
    const nonSensitiveLabels = ['policy', 'plan', 'carrier', 'coverage', 'premium', 'affiliation',
      'source', 'issue date', 'effective date', 'due', 'benefi', 'hobby', 'county', 'lender',
      'mortgage', 'product', 'alias', 'status', 'lead type', 'lead number', 'campaign',
      ...PHONE_LABEL_HINTS];
    if (nonSensitiveLabels.some(ns => labelLower.includes(ns))) {
      return { masked: trimmed, isSensitive: false };
    }

    // ── SSN: detect by label ("SSN", "Ssn", "Social Security", "ss") or by pattern ──
    const ssnLabels = ['ssn', 'social security', 'ss#', 'ss number'];
    const isSsnField = ssnLabels.some(sl => labelLower === sl || labelLower.includes(sl));
    // Pattern: 9 digits with optional separators (123-45-6789, 123456789, 123 45 6789)
    const isSsnPattern = /^\d{3}[-\s]?\d{2}[-\s]?\d{4}$/.test(trimmed);
    if (isSsnPattern || (isSsnField && /^\d{3}[-.\s]?\d{2}[-.\s]?\d{4}$/.test(trimmed))) {
      const last4 = trimmed.slice(-4);
      return { masked: `***-**-${last4}`, isSensitive: true, type: 'SSN' };
    }

    // ── Credit Card: 16 digits with optional separators ──
    if (/^\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}$/.test(trimmed)) {
      const last4 = trimmed.slice(-4);
      return { masked: `****-****-****-${last4}`, isSensitive: true, type: 'Credit Card' };
    }

    // ── Driver's License: only if field label suggests DL ──
    // Real prod labels: "LICENSE", also handles "DL", "Driver License", etc.
    const dlLabels = ['dl', 'driver', 'license', 'licence', 'state id'];
    if (dlLabels.some(dl => labelLower.includes(dl)) && /^[A-Za-z]{0,3}[-\s]?\d{4,14}$/.test(trimmed)) {
      const last4 = trimmed.slice(-4);
      return { masked: `${'*'.repeat(Math.max(trimmed.length - 4, 4))}${last4}`, isSensitive: true, type: "Driver's License" };
    }

    // ── Bank Account / Routing Number ──
    // Real prod labels: "Routing", "Route", "Account", "Acct", "Bank Name", "ACCOUNT NUMBER LAST 4"
    const financialLabels = ['account', 'acct', 'routing', 'route', 'bank', 'aba', 'checking', 'savings'];
    const isFinancialField = financialLabels.some(fl => labelLower.includes(fl));
    // Financial-labeled fields: mask if 4+ digits; unlabeled: mask if 10+ digits
    const digitsOnly = trimmed.replace(/\D/g, '');
    if (/^\d+$/.test(trimmed) && digitsOnly.length >= 4) {
      if (isFinancialField && digitsOnly.length >= 4) {
        const last4 = trimmed.slice(-4);
        const maskLen = Math.max(trimmed.length - 4, 1);
        return { masked: `${'*'.repeat(maskLen)}${last4}`, isSensitive: true, type: labelLower.includes('rout') ? 'Routing Number' : 'Account Number' };
      }
      if (digitsOnly.length >= 10 && !isSsnPattern) {
        const last4 = trimmed.slice(-4);
        return { masked: `${'*'.repeat(trimmed.length - 4)}${last4}`, isSensitive: true, type: 'Account Number' };
      }
    }

    return { masked: trimmed, isSensitive: false };
  };

  // Toggle field reveal/hide
  const toggleFieldReveal = (fieldName: string) => {
    setRevealedFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  };

  // Handle field value change
  const handleFieldChange = (fieldName: string, value: any) => {
    const currentCustomFields = form.getValues('customFields') || {};
    form.setValue('customFields', {
      ...currentCustomFields,
      [fieldName]: value
    }, { shouldDirty: true });
  };

  // Get field value from customFields (using watched value for reactivity)
  const getFieldValue = (fieldName: string) => {
    return watchedCustomFields[fieldName] ?? '';
  };

  // In VIEW mode, omit empty custom fields — fields with no value are hidden so
  // the detail view isn't cluttered with blanks. In EDIT mode every field is
  // shown so it stays fillable/removable.
  const isEmptyValue = (v: unknown) =>
    v === undefined || v === null || String(v).trim() === '';
  const visibleFields = isEditing
    ? customFields
    : customFields.filter((f) => !isEmptyValue(watchedCustomFields[f.name]));

  // Group synonym / typo / case variants under one label ("CARREIR", "COMPANY"
  // → Carrier) and keep the importer's policy-slot number ("Carrier 2"). The
  // raw key stays on `field.name` — that is what edits and removals write to.
  const describedFields = visibleFields.map((field) => {
    const d = describeCustomKey(field.name, field.label);
    return { field: { ...field, label: d.label }, ...d };
  });
  const keyFieldOrder = (c: string) => (KEY_FIELD_CANONICALS as readonly string[]).indexOf(c);
  const sortedFields = [...describedFields].sort((a, b) => {
    // Key fields first, in Carrier → Annual → Monthly order, policy 1 before 2;
    // everything else keeps its stored order.
    if (a.isKeyField !== b.isKeyField) return a.isKeyField ? -1 : 1;
    if (a.isKeyField && b.isKeyField) {
      return (a.slot ?? 1) - (b.slot ?? 1) || keyFieldOrder(a.canonical) - keyFieldOrder(b.canonical);
    }
    return 0;
  });
  const keyFieldCount = sortedFields.filter((d) => d.isKeyField).length;
  // Never show an empty section because of the toggle: a lead with none of the
  // key fields falls back to everything.
  const keyOnlyActive = keyFieldsOnly && keyFieldCount > 0;
  const shownFields = keyOnlyActive ? sortedFields.filter((d) => d.isKeyField) : sortedFields;
  const hiddenCount = sortedFields.length - shownFields.length;

  // Render input based on field type
  const renderFieldInput = (field: CustomField) => {
    const value = getFieldValue(field.name);
    const stringValue = String(value ?? '');
    const { masked, isSensitive, type: sensitiveType } = maskValue(stringValue, field.label);
    const isFieldRevealed = revealedFields.has(field.name);

    // In view mode with sensitive data: show masked value with Eye toggle
    if (!isEditing && isSensitive) {
      return (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel>{field.label}</FormLabel>
            <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0" variant="secondary">
              <Shield className="h-2.5 w-2.5 mr-0.5" />
              {sensitiveType}
            </Badge>
          </div>
          <div className="flex items-center gap-1">
            <FormControl>
              <Input
                value={isFieldRevealed ? stringValue : masked}
                disabled
                className="font-mono"
              />
            </FormControl>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 shrink-0"
              onClick={() => toggleFieldReveal(field.name)}
              title={isFieldRevealed ? 'Hide sensitive data' : 'Reveal sensitive data'}
            >
              {isFieldRevealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </FormItem>
      );
    }

    // Phone-ish field in view mode: show it formatted, and dial it against THIS
    // lead so the call logs on the same record as the primary number.
    const dialable = !isEditing ? dialableDigits(field.label, stringValue) : null;
    if (dialable) {
      return (
        <FormItem>
          <FormLabel>{field.label}</FormLabel>
          <div className="flex items-center gap-1">
            <FormControl>
              <Input value={formatPhoneDisplay(dialable)} disabled />
            </FormControl>
            {onCallNumber && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 shrink-0 p-0 text-primary hover:bg-primary/10"
                onClick={() => onCallNumber(dialable)}
                title={`Call ${formatPhoneDisplay(dialable)}`}
                aria-label={`Call ${field.label} at ${formatPhoneDisplay(dialable)}`}
              >
                <PhoneCall className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </FormItem>
      );
    }

    const commonProps = {
      value: value,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        handleFieldChange(field.name, e.target.value),
      disabled: !isEditing,
      placeholder: `Enter ${field.label.toLowerCase()}`
    };

    switch (field.type) {
      case 'textarea':
        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Textarea {...commonProps} rows={3} />
            </FormControl>
          </FormItem>
        );
      case 'number':
        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Input
                {...commonProps}
                type="number"
                onChange={(e) => handleFieldChange(field.name, e.target.value ? Number(e.target.value) : '')}
              />
            </FormControl>
          </FormItem>
        );
      case 'email':
        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Input {...commonProps} type="email" />
            </FormControl>
          </FormItem>
        );
      case 'phone':
        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Input
                value={value}
                onChange={(e) => handlePhoneChange(field.name, e.target.value)}
                disabled={!isEditing}
                placeholder="(xxx) xxx-xxxx"
                type="tel"
                maxLength={14}
              />
            </FormControl>
          </FormItem>
        );
      case 'date':
        const { minDate, maxDate } = getDateConstraints();
        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <Popover>
              <FormControl>
                <div className="relative">
                  <Input
                    value={value}
                    onChange={(e) => {
                      let dateValue = e.target.value;
                      // Limit year to 4 digits max and validate range
                      if (dateValue) {
                        const parts = dateValue.split('-');
                        if (parts[0]) {
                          if (parts[0].length > 4) {
                            parts[0] = parts[0].slice(0, 4);
                            dateValue = parts.join('-');
                          }
                          const year = parseInt(parts[0], 10);
                          const currentYear = new Date().getFullYear();
                          if (year > currentYear) {
                            parts[0] = String(currentYear);
                            dateValue = parts.join('-');
                          }
                        }
                      }
                      handleFieldChange(field.name, dateValue);
                    }}
                    onBlur={(e) => {
                      const dateValue = e.target.value;
                      if (dateValue) {
                        if (dateValue > maxDate) {
                          handleFieldChange(field.name, maxDate);
                        } else if (dateValue < minDate) {
                          handleFieldChange(field.name, minDate);
                        }
                      }
                    }}
                    disabled={!isEditing}
                    type="date"
                    min={minDate}
                    max={maxDate}
                    className="w-full pr-10"
                  />
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      disabled={!isEditing}
                    >
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                </div>
              </FormControl>
              <PopoverContent align="start" className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={value ? new Date(value + 'T12:00:00') : undefined}
                  onSelect={(date) => {
                    if (date) {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      handleFieldChange(field.name, `${year}-${month}-${day}`);
                    }
                  }}
                  disabled={(date) => {
                    const min = new Date(minDate + 'T00:00:00');
                    const max = new Date(maxDate + 'T23:59:59');
                    return date > max || date < min;
                  }}
                  defaultMonth={value ? new Date(value + 'T12:00:00') : new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </FormItem>
        );
      default:
        return (
          <FormItem>
            <FormLabel>{field.label}</FormLabel>
            <FormControl>
              <Input {...commonProps} type="text" />
            </FormControl>
          </FormItem>
        );
    }
  };

  const fieldsContent = (
    <div className="space-y-4">
      {/* Key-fields toggle + (bare mode) Add Field. Always visible when the lead
          has any fields, so it is obvious WHY the list is short or long. */}
      {(sortedFields.length > 0 || (bare && isEditing)) && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {keyFieldCount > 0 ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setKeyFieldsOnly(!keyFieldsOnly)}
                className="h-7 px-2.5 text-xs"
                title={keyOnlyActive ? `Showing ${KEY_FIELD_LABELS}` : 'Show only Carrier and premium fields'}
              >
                {keyOnlyActive ? (
                  <><Eye className="h-3.5 w-3.5 mr-1" />Show all fields ({hiddenCount} more)</>
                ) : (
                  <><EyeOff className="h-3.5 w-3.5 mr-1" />Show key fields only</>
                )}
              </Button>
              <span className="text-[11px] text-muted-foreground">
                {keyOnlyActive ? `Showing ${KEY_FIELD_LABELS}` : `All ${sortedFields.length} fields`}
              </span>
            </div>
          ) : sortedFields.length > 0 ? (
            <span className="text-[11px] text-muted-foreground">
              No Carrier or premium fields on this lead — showing all {sortedFields.length}
            </span>
          ) : <span />}
          {bare && isEditing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAddField(true)}
              className="h-8 px-2"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Field
            </Button>
          )}
        </div>
      )}

      {/* Existing custom fields (empty ones hidden in view mode) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shownFields.map(({ field, aliasedFrom }) => (
          <div key={field.id} className="relative">
            {renderFieldInput(field)}
            {aliasedFrom && (
              <p className="mt-1 text-[10px] leading-none text-muted-foreground" title="This value was imported under a different column name">
                imported as &ldquo;{aliasedFrom}&rdquo;
              </p>
            )}
            {isEditing && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveField(field.id)}
                className="absolute top-0 right-0 h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                title="Remove field"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Add new field form */}
      {showAddField && isEditing && (
        <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">Add New Field</h4>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddField(false);
                setNewFieldName('');
                setNewFieldLabel('');
                setNewFieldType('text');
              }}
              className="h-6 w-6 p-0"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="fieldName" className="text-xs">Field Name (internal)</Label>
              <Input
                id="fieldName"
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="preferred_contact_time"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="fieldLabel" className="text-xs">Display Label</Label>
              <Input
                id="fieldLabel"
                value={newFieldLabel}
                onChange={(e) => setNewFieldLabel(e.target.value)}
                placeholder="Preferred Contact Time"
                className="h-8 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="fieldType" className="text-xs">Field Type</Label>
              <select
                id="fieldType"
                value={newFieldType}
                onChange={(e) => setNewFieldType(e.target.value as any)}
                className="h-8 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
              >
                <option value="text">Text</option>
                <option value="textarea">Textarea</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={handleAddField}
              size="sm"
              className="h-8"
            >
              Add Field
            </Button>
          </div>
        </div>
      )}

      {shownFields.length === 0 && !showAddField && (
        <div className="text-center py-4 text-muted-foreground">
          <p className="text-sm">
            {isEditing || customFields.length === 0
              ? 'No custom fields added yet.'
              : 'No additional information.'}
          </p>
          {isEditing && (
            <p className="text-xs mt-1">Click &quot;Add Field&quot; to create your first custom field.</p>
          )}
        </div>
      )}
    </div>
  );

  if (bare) return fieldsContent;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Info className="h-5 w-5 text-blue-600" />
          </div>
          <CardTitle className="text-lg font-semibold text-gray-900">
            More Information
          </CardTitle>
        </div>
        {isEditing && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAddField(true)}
            className="h-8 px-2"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Field
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {fieldsContent}
      </CardContent>
    </Card>
  );
};